import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Text,
  Button,
  Alert,
  AlertIcon,
  Input,
  InputGroup,
  InputRightElement,
  Divider,
  useToast,
  Flex,
  Heading,
  Select,
  Icon,
  Spacer
} from '@chakra-ui/react';
import { FaSearchPlus } from 'react-icons/fa';
import { react_api } from '../../../../config';

const Edit = ({ currentSummary, visualization, onGenerateVisualization, colors, username, bearerToken }) => {
  // State
  const [instruction, setInstruction] = useState('');
  const [library, setLibrary] = useState('seaborn'); // Default a seaborn
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  // Theme colors
  const themeColors = colors || {
    navbarBg: '#0b4e5aff',
    fontColor: '#0b4e5aff',
    borderColor: '#178da1ff',
    buttonColor: '#083c46ff',
    iconColor: '#9acd32',
    titleIconColor: '#9acd32',
    servicesBg: '#f6f3ec'
  };

  // Function to generate a visualization from natural language instructions
  const handleGenerateFromPrompt = async () => {
    // Validate input
    if (!instruction.trim()) {
      toast({
        title: "Input missing",
        description: "Enter a description of the visualization you want to create",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Prepare the request for the edit API in the correct format
      const editPayload = {
        username: username,
        BearerToken: bearerToken,
        service: 3,
        request: {
          summary: currentSummary?.summary,
          instructions: [instruction],
          library: library,
          code: visualization && visualization[0]?.code || ""
        }
      };

      // Call the edit API
      const response = await axios.post(
        `${react_api}/externalService/novaServices`,
        editPayload,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      // Process the response
      if (response.data && response.data.request && response.data.request.charts) {
        const charts = response.data.request.charts;
        
        if (charts.length > 0 && charts[0].code) {
          // Pass the charts to the parent component
          onGenerateVisualization(charts);

          // Feedback to the user
          toast({
            title: "Visualization created",
            description: "Your visualization has been generated successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
          });

          // Reset the input
          setInstruction('');
        } else {
          throw new Error('Invalid response format or missing code');
        }
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (err) {
      console.error('Error generating visualization from prompt:', err);
      
      let errorMessage = 'Failed to generate visualization from prompt. Please try again.';
      
      if (err.response?.status === 422) {
        const errorDetail = err.response?.data?.detail; 
        if (Array.isArray(errorDetail) && errorDetail.length > 0) {
          errorMessage = errorDetail[0]?.msg || 'Validation error';
        } else if (typeof errorDetail === 'string') {
          errorMessage = errorDetail;
        }
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box mb={4} p={3} borderWidth="1px" borderRadius="lg" borderColor={themeColors.borderColor}>
      <Flex alignItems="center" mb={3}>
        <Heading size="sm" color={themeColors.fontColor}>Describe in natural language the visualization you want to create</Heading>
        <Spacer />
        <Flex alignItems="center">
          <Text fontSize="sm" mr={2} color="gray.600">Library:</Text>
          <Select 
            size="sm" 
            value={library} 
            onChange={(e) => setLibrary(e.target.value)}
            disabled={isLoading}
            width="120px"
            borderColor={themeColors.borderColor}
            _hover={{ borderColor: themeColors.navbarBg }}
            _focus={{ borderColor: themeColors.navbarBg }}
          >
            <option value="seaborn">Seaborn</option>
            <option value="matplotlib">Matplotlib</option>
          </Select>
        </Flex>
      </Flex>
      {error && (
        <Alert status="error" mb={3} fontSize="sm" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}
      
      <form onSubmit={(e) => {
        e.preventDefault();
        handleGenerateFromPrompt();
      }}>
        <InputGroup size="md" mb={3}>
          <Input
            placeholder="Es. How does the price of vehicles vary by type? Plot this in a bar chart"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            disabled={isLoading}
            pr="3.5rem"
            borderColor={themeColors.borderColor}
            _hover={{ borderColor: themeColors.navbarBg }}
            _focus={{ borderColor: themeColors.navbarBg, boxShadow: `0 0 0 1px ${themeColors.navbarBg}` }}
          />
          <InputRightElement width="3rem" pr={2}>
            <Button
              h="1.75rem"
              size="sm"
              type="submit"
              isLoading={isLoading}
              bg={themeColors.buttonColor}
              color="white"
              _hover={{ bg: themeColors.navbarBg }}
              aria-label="Generate visualization"
              title="Generate visualization"
              borderRadius="md"
              px={2}
            >
              <Icon as={FaSearchPlus} />
            </Button>
          </InputRightElement>
        </InputGroup>
      </form>
      <Divider my={4} />
      <Text fontSize="xs" color="gray.500">
        Tip: Try to be specific about the type of chart you want and which variables to include.
      </Text>
    </Box>
  );
};

export default Edit;
